import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { makeStyles } from "components/providers/makeStyles";
import { keyframes } from "tss-react";
import * as NotificationActions from "../../../actions/notification";
import { createEntries } from "../../../actions/entry";
import { T } from "../../util/t";
import { AutoProgressButton } from "../forms/fields/AutoProgressButton";
import { useTakeoverDialogStyles } from "../forms/SearchTakeoverDialog";
import { useVisualViewportHeight } from "hooks/useVisualViewportHeight";
import { AthletesListSearch } from "../athletes/AthletesListSearch";
import { AddEntryDialogContentItem } from "./AddEntryDialogContentItem";

const useStyles = makeStyles(theme => ({
    container: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0
    },
    header: {
        marginBottom: 0
    },
    content: {
        "&&": {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            paddingBottom: 0
        }
    },
    searchForm: {
        marginTop: theme.spacing(.25),
        marginBottom: theme.spacing(.25),
        "& > div": {
            flexShrink: 0,
            height: theme.spacing(6),
            marginBottom: theme.spacing(1),
        }
    },
    dialogActions: {
        "&&": {
            padding: theme.spacing(0, 3, 2, 3),
            animation: `${keyframes`
                from {
                    opacity: 0;
                    transform: translate3d(0, 100%, 0);
                }
                to {
                    opacity: 1;
                    transform: translate3d(0, 0, 0);
                }
            `} .1s ease`
        }
    }
}));

export const AddEntryDialogContent = ({ eventDivisionId, isTeams, closeDialog, registered = [], alternates = [] }) => {
    const contentRef = useRef();
    const dispatch = useDispatch();
    const [selected, setSelected] = useState({});
    const [newItems, setNewItems] = useState({});
    const classes = { ...useTakeoverDialogStyles(), ...useStyles() };

    const containerHeight = useVisualViewportHeight();

    const submit = () =>
        dispatch(createEntries(eventDivisionId, Object.keys(selected).map(value => selected[value] === "isNew" ? { athlete_name: value } : { athlete_id: value }), isTeams))
            .then(closeDialog)
            .catch(e => dispatch(NotificationActions.error("Oops, something went wrong!")));

    const selectItem = (item, selected) => {
        if (item.isNew) setNewItems(newItems => ({ ...newItems, [item.id]: item }));
        setSelected(prevState => {
            const newState = { ...prevState };
            selected ? newState[item.id] = item.isNew ? "isNew" : true : delete newState[item.id];
            return newState;
        });
    };

    return (
        <div className={classes.container} style={{ maxHeight: containerHeight }}>
            <DialogTitle id="add-athlete-dialog-title" className={`${classes.title} ${classes.header}`}>
                <Typography variant="label1">{isTeams ? <T>Add teams</T> : <T>Add athletes</T>}</Typography>
                <IconButton color="inherit" size="small" onClick={closeDialog}><Close/></IconButton>
            </DialogTitle>

            <DialogContent className={`${classes.dialogContent} ${classes.content}`} ref={contentRef}>
                <AthletesListSearch
                    placeholder={isTeams ? "Enter team name..." : "Enter athlete name..."}
                    searchFormClassName={classes.searchForm}
                    newItems={newItems}
                    canCreateItem
                    renderItem={item =>
                        <AddEntryDialogContentItem
                            item={item}
                            isTeams={isTeams}
                            selected={selected}
                            selectItem={selectItem}
                            registered={registered}
                            alternates={alternates}
                        />}
                />
            </DialogContent>

            {Object.keys(selected).length > 0 &&
            <DialogActions className={classes.dialogActions}>
                <AutoProgressButton fullWidth color="primary" variant="contained" onClick={submit}>
                    <T value={Object.keys(selected).length}>{isTeams ? "AddEntryWidget_add_teams" : "AddEntryWidget_add_athletes"}</T>
                </AutoProgressButton>
            </DialogActions>}
        </div>
    );
};
